if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.zoomScale = 1;
        this.minZoom = 1;
        this.maxZoom = 2.5;
        this.zoomStep = 0.25;

        this.addEventListener('click', this.onModalClick.bind(this));
        this.addEventListener('keydown', this.onKeyDown.bind(this));
      }

      hide() {
        this.resetZoom();
        super.hide();
      }

      show(opener) {
        super.show(opener);
        this.showActiveMedia();
      }

      onModalClick(event) {
        const target = event.target.closest(
          '.product-media-modal__nav--prev, .product-media-modal__nav--next, .product-media-modal__thumbnail, .product-media-modal__zoom-in, .product-media-modal__zoom-out'
        );
        if (!target) return;

        event.preventDefault();
        event.stopPropagation();

        if (target.classList.contains('product-media-modal__nav--prev')) {
          this.changeSlide(-1);
        } else if (target.classList.contains('product-media-modal__nav--next')) {
          this.changeSlide(1);
        } else if (target.classList.contains('product-media-modal__thumbnail')) {
          this.setActiveSlideById(target.dataset.mediaId);
        } else if (target.classList.contains('product-media-modal__zoom-in')) {
          this.adjustZoom(this.zoomStep);
        } else if (target.classList.contains('product-media-modal__zoom-out')) {
          this.adjustZoom(-this.zoomStep);
        }
      }

      onKeyDown(event) {
        if (!this.hasAttribute('open')) return;

        if (event.code === 'ArrowLeft') {
          event.preventDefault();
          this.changeSlide(-1);
        } else if (event.code === 'ArrowRight') {
          event.preventDefault();
          this.changeSlide(1);
        }
      }

      getVisibleSlidesList() {
        return Array.from(this.querySelectorAll('.product-media-modal__slide')).filter((slide) => {
          return !slide.classList.contains('product__media-item--variant') || slide.classList.contains('active');
        });
      }

      changeSlide(direction) {
        const slides = this.getVisibleSlidesList();
        const activeIndex = slides.findIndex((slide) => slide.classList.contains('active'));
        if (activeIndex === -1) return;

        let nextIndex = activeIndex + direction;
        if (nextIndex < 0) nextIndex = slides.length - 1;
        if (nextIndex >= slides.length) nextIndex = 0;

        this.setActiveSlide(slides[nextIndex]);
      }

      setActiveSlideById(mediaId) {
        const slide = this.querySelector(`.product-media-modal__slide[data-media-id="${mediaId}"]`);
        if (slide) this.setActiveSlide(slide);
      }

      setActiveSlide(slide) {
        if (!slide) return;

        this.resetZoom();

        this.querySelectorAll('.product-media-modal__slide').forEach((element) => {
          element.classList.remove('active');
        });

        slide.classList.add('active');

        const mediaId = slide.dataset.mediaId;
        this.querySelectorAll('.product-media-modal__thumbnail').forEach((thumb) => {
          if (thumb.dataset.mediaId === mediaId) {
            thumb.setAttribute('aria-current', 'true');
          } else {
            thumb.removeAttribute('aria-current');
          }
        });

        this.updateNavState();

        const deferred = slide.querySelector('deferred-media, product-model');
        const template = deferred?.querySelector('template');
        if (deferred && template?.content?.querySelector('.js-youtube')) {
          deferred.loadContent();
        }
      }

      updateNavState() {
        const slides = this.getVisibleSlidesList();
        const prev = this.querySelector('.product-media-modal__nav--prev');
        const next = this.querySelector('.product-media-modal__nav--next');
        if (prev) prev.style.visibility = slides.length > 1 ? 'visible' : 'hidden';
        if (next) next.style.visibility = slides.length > 1 ? 'visible' : 'hidden';
      }

      showActiveMedia() {
        const mediaId = this.openedBy?.getAttribute('data-media-id');
        const slide =
          this.querySelector(`.product-media-modal__slide[data-media-id="${mediaId}"]`) ||
          this.querySelector('.product-media-modal__slide');

        this.setActiveSlide(slide);
      }

      adjustZoom(delta) {
        this.zoomScale = Math.min(this.maxZoom, Math.max(this.minZoom, this.zoomScale + delta));
        this.applyZoomToActiveSlide();
        this.updateZoomButtons();
      }

      applyZoomToActiveSlide() {
        const activeSlide = this.querySelector('.product-media-modal__slide.active');
        const content = this.querySelector('.product-media-modal__content');
        if (!activeSlide || !content) return;

        const img = activeSlide.querySelector('img');
        if (!img) return;

        const clearZoom = () => {
          img.style.width = '';
          img.style.height = '';
          img.style.maxWidth = '';
          img.style.maxHeight = '';
          img.style.transform = '';
          content.classList.remove('is-zoomed');
          content.scrollTop = 0;
          content.scrollLeft = 0;
        };

        if (this.zoomScale <= 1) {
          clearZoom();
          return;
        }

        const setDimensions = () => {
          const naturalWidth = img.naturalWidth;
          const naturalHeight = img.naturalHeight;
          if (!naturalWidth || !naturalHeight) return;

          const contentRect = content.getBoundingClientRect();
          const maxBaseW = Math.max(contentRect.width - 32, 1);
          const maxBaseH = Math.max(contentRect.height - 32, 1);
          const fitScale = Math.min(1, maxBaseW / naturalWidth, maxBaseH / naturalHeight);
          const displayW = Math.round(naturalWidth * fitScale * this.zoomScale);
          const displayH = Math.round(naturalHeight * fitScale * this.zoomScale);

          img.style.maxWidth = 'none';
          img.style.maxHeight = 'none';
          img.style.width = `${displayW}px`;
          img.style.height = `${displayH}px`;
          img.style.transform = '';
          content.classList.add('is-zoomed');
        };

        if (img.complete) {
          setDimensions();
        } else {
          img.onload = () => setDimensions();
        }
      }

      resetZoom() {
        this.zoomScale = 1;
        const content = this.querySelector('.product-media-modal__content');
        this.querySelectorAll('.product-media-modal__slide').forEach((slide) => {
          slide.style.transform = '';
          slide.querySelectorAll('img').forEach((img) => {
            img.style.width = '';
            img.style.height = '';
            img.style.maxWidth = '';
            img.style.maxHeight = '';
            img.style.transform = '';
          });
        });
        content?.classList.remove('is-zoomed');
        if (content) {
          content.scrollTop = 0;
          content.scrollLeft = 0;
        }
        this.updateZoomButtons();
      }

      updateZoomButtons() {
        const zoomIn = this.querySelector('.product-media-modal__zoom-in');
        const zoomOut = this.querySelector('.product-media-modal__zoom-out');
        if (zoomIn) zoomIn.disabled = this.zoomScale >= this.maxZoom;
        if (zoomOut) zoomOut.disabled = this.zoomScale <= this.minZoom;
      }
    }
  );
}
